// @oasis-permission-inherits: /videos (mirage-videos:view)
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Loader2, RefreshCw, Upload, XCircle } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";
import { uploadResumableFile } from "~/modules/file-upload/resumable-upload";
import {
  extensionToMimeType,
  formatFileSize,
  mimeTypeToResumableAssetType,
} from "~/modules/file-upload/utils";
import { MirageEvalMetric, MirageEvalStatusBadge } from "~/modules/mirage-video-evals/components";
import { formatMirageEvalDate } from "~/modules/mirage-video-evals/formatters";
import {
  normalizeManifest,
  prepareMirageVideoEvalImport,
  withUploadedMirageEvalAssetId,
} from "~/modules/mirage-video-evals/manifest";
import { importMirageVideoEvalTestMutation } from "~/modules/mirage-video-evals/mutations";
import { listMirageVideoEvalTestsQuery } from "~/modules/mirage-video-evals/queries";

const DEFAULT_BUCKET = "captions-mirage-evals";
const MAX_UPLOAD_ATTEMPTS = 3;
const UPLOAD_CONCURRENCY = 4;

interface UploadFailure {
  attempts: number;
  fileName: string;
  message: string;
  videoId: string;
  videoIndex: number;
}

interface UploadRunProgress {
  activeUploads: number;
  completedBytes: number;
  completedUploads: number;
  currentFileNames: string[];
  failedUploads: number;
  totalBytes: number;
  totalUploads: number;
}

export const Route = createFileRoute("/_dash/videos/evals/")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const manifestInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [manifestText, setManifestText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadRunProgress, setUploadRunProgress] = useState<UploadRunProgress | null>(null);
  const [uploadedAssetIdsByVideoIndex, setUploadedAssetIdsByVideoIndex] = useState<
    Record<number, string>
  >({});
  const [uploadFailures, setUploadFailures] = useState<UploadFailure[]>([]);

  const { data, isLoading, error, refetch, isFetching } = listMirageVideoEvalTestsQuery.useQuery({
    params: { limit: 50 },
  });
  const tests = data?.tests ?? [];

  const handleManifestFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    resetImportSession();
    setManifestText(await file.text());
  };

  const handleVideoFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    resetImportSession();
    setSelectedFiles(Array.from(event.target.files ?? []));
  };

  const handleManifestTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    resetImportSession();
    setManifestText(event.target.value);
  };

  const resetImportSession = () => {
    setUploadedAssetIdsByVideoIndex({});
    setUploadFailures([]);
    setUploadRunProgress(null);
    setUploadStatus("");
    setUploadProgress(0);
  };

  const handleImport = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let keepImportState = false;
    setIsImporting(true);
    setUploadProgress(0);
    setUploadStatus("Validating manifest");
    setUploadFailures([]);

    try {
      const manifest = normalizeManifest(JSON.parse(manifestText));
      const prepared = prepareMirageVideoEvalImport({
        defaultBucket: DEFAULT_BUCKET,
        files: selectedFiles,
        manifest,
      });
      let request = prepared.request;
      const uploadedAssetIds = { ...uploadedAssetIdsByVideoIndex };
      for (const [videoIndex, assetId] of Object.entries(uploadedAssetIds)) {
        request = withUploadedMirageEvalAssetId({
          assetId,
          request,
          videoIndex: Number(videoIndex),
        });
      }

      const uploadByIndex = new Map(
        prepared.uploads.map((upload) => [upload.videoIndex, upload] as const)
      );
      const alreadyUploadedIndexes = Object.keys(uploadedAssetIds)
        .map(Number)
        .filter((videoIndex) => uploadByIndex.has(videoIndex));
      const totalBytes = prepared.uploads.reduce((sum, upload) => sum + upload.file.size, 0);
      let completedBytes = alreadyUploadedIndexes.reduce(
        (sum, videoIndex) => sum + (uploadByIndex.get(videoIndex)?.file.size ?? 0),
        0
      );
      let completedUploads = alreadyUploadedIndexes.length;
      const failures: UploadFailure[] = [];
      const activeFileNamesByVideoIndex = new Map<number, string>();
      const activeProgressByVideoIndex = new Map<number, number>();

      const updateRunProgress = (partial?: { failedUploads?: number }) => {
        const activeBytes = [...activeProgressByVideoIndex].reduce(
          (sum, [videoIndex, progress]) => {
            const activeUpload = uploadByIndex.get(videoIndex);
            return sum + (activeUpload?.file.size ?? 0) * progress;
          },
          0
        );
        const visibleActiveFileNames = [...activeFileNamesByVideoIndex.values()].slice(0, 3);
        const currentCompletedBytes = completedBytes + activeBytes;
        setUploadProgress(totalBytes > 0 ? currentCompletedBytes / totalBytes : 1);
        setUploadRunProgress({
          activeUploads: activeFileNamesByVideoIndex.size,
          completedBytes: currentCompletedBytes,
          completedUploads,
          currentFileNames: visibleActiveFileNames,
          failedUploads: partial?.failedUploads ?? failures.length,
          totalBytes,
          totalUploads: prepared.uploads.length,
        });
        if (activeFileNamesByVideoIndex.size > 0) {
          setUploadStatus(
            `Uploading ${activeFileNamesByVideoIndex.size} files (${completedUploads}/${prepared.uploads.length} done)`
          );
        }
      };

      updateRunProgress();
      setUploadProgress(totalBytes > 0 ? completedBytes / totalBytes : 1);

      const uploadsToRun = prepared.uploads.filter(
        (upload) => !uploadedAssetIds[upload.videoIndex]
      );
      let nextUploadIndex = 0;
      const workerCount = Math.min(UPLOAD_CONCURRENCY, uploadsToRun.length);

      async function uploadWorker() {
        while (nextUploadIndex < uploadsToRun.length) {
          const upload = uploadsToRun[nextUploadIndex];
          nextUploadIndex += 1;
          await uploadOne(upload);
        }
      }

      async function uploadOne(upload: (typeof prepared.uploads)[number]) {
        const video = prepared.request.videos[upload.videoIndex];
        activeFileNamesByVideoIndex.set(upload.videoIndex, upload.file.name);
        activeProgressByVideoIndex.set(upload.videoIndex, 0);
        updateRunProgress();
        try {
          const { assetId, attempts } = await uploadEvalVideoWithRetries({
            bucket: upload.bucket,
            file: upload.file,
            prefix: upload.prefix,
            onAttempt: () => updateRunProgress(),
            onProgress: (fileProgress) => {
              activeProgressByVideoIndex.set(upload.videoIndex, fileProgress);
              updateRunProgress();
            },
          });

          uploadedAssetIds[upload.videoIndex] = assetId;
          setUploadedAssetIdsByVideoIndex({ ...uploadedAssetIds });
          completedBytes += upload.file.size;
          completedUploads += 1;
          activeFileNamesByVideoIndex.delete(upload.videoIndex);
          activeProgressByVideoIndex.delete(upload.videoIndex);
          updateRunProgress();
          if (attempts > 1) {
            toast.success(`Recovered upload after ${attempts} attempts`, {
              description: upload.file.name,
            });
          }
        } catch (error) {
          const failure = {
            attempts: MAX_UPLOAD_ATTEMPTS,
            fileName: upload.file.name,
            message: error instanceof Error ? error.message : "Unknown upload error",
            videoId: video?.videoId ?? String(upload.videoIndex + 1),
            videoIndex: upload.videoIndex,
          };
          failures.push(failure);
          setUploadFailures([...failures]);
          activeFileNamesByVideoIndex.delete(upload.videoIndex);
          activeProgressByVideoIndex.delete(upload.videoIndex);
          updateRunProgress({ failedUploads: failures.length });
        }
      }

      if (workerCount > 0) {
        await Promise.all(Array.from({ length: workerCount }, () => uploadWorker()));
      }

      if (failures.length > 0) {
        keepImportState = true;
        setUploadStatus(`${failures.length} uploads failed`);
        toast.error(`${failures.length} uploads failed`, {
          description: "Successful uploads were kept. Press Retry Failed Uploads to continue.",
        });
        return;
      }

      setUploadStatus("Publishing test");
      keepImportState = true;
      for (const [videoIndex, assetId] of Object.entries(uploadedAssetIds)) {
        request = withUploadedMirageEvalAssetId({
          assetId,
          request,
          videoIndex: Number(videoIndex),
        });
      }

      await importMirageVideoEvalTestMutation.trigger({ params: request });
      keepImportState = false;
      toast.success("Mirage eval imported");
      setManifestText("");
      setSelectedFiles([]);
      if (manifestInputRef.current) {
        manifestInputRef.current.value = "";
      }
      if (videoInputRef.current) {
        videoInputRef.current.value = "";
      }
      await navigate({
        to: "/videos/evals/$testId",
        params: { testId: request.testId },
      });
    } catch (error) {
      if (keepImportState) {
        setUploadStatus("Publishing failed");
      }
      toast.error("Import failed", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsImporting(false);
      if (!keepImportState) {
        setUploadStatus("");
        setUploadProgress(0);
        setUploadRunProgress(null);
        setUploadedAssetIdsByVideoIndex({});
        setUploadFailures([]);
      }
    }
  };

  const uploadedCount = Object.keys(uploadedAssetIdsByVideoIndex).length;
  const hasRecoverableUploadState = uploadedCount > 0 || uploadFailures.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold">Mirage Eval Ranking</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <span>{tests.length} tests</span>
            <span>|</span>
            <span>{DEFAULT_BUCKET}</span>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn("size-4", isFetching && "animate-spin")} />
          Refresh
        </Button>
      </div>

      <form onSubmit={handleImport} className="rounded-md border bg-card p-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-2">
            <Label htmlFor="manifest">Test manifest JSON</Label>
            <Textarea
              id="manifest"
              value={manifestText}
              onChange={handleManifestTextChange}
              disabled={isImporting}
              className="min-h-56 font-mono text-xs"
              placeholder='{"testId":"clip1-sweep","name":"Clip 1 sweep","videos":[...]}'
            />
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="manifest-file">Manifest file</Label>
              <Input
                ref={manifestInputRef}
                id="manifest-file"
                type="file"
                accept="application/json,.json"
                disabled={isImporting}
                onChange={handleManifestFileChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="video-files">Video files or folder</Label>
              <Input
                ref={videoInputRef}
                id="video-files"
                type="file"
                multiple
                directory=""
                webkitdirectory=""
                accept="video/mp4,video/quicktime,video/webm"
                disabled={isImporting}
                onChange={handleVideoFilesChange}
              />
              {selectedFiles.length > 0 && (
                <div className="max-h-32 overflow-auto rounded-md border bg-muted/20 p-2 text-xs">
                  {selectedFiles.map((file) => (
                    <div key={`${file.name}-${file.size}`} className="flex justify-between gap-2">
                      <span className="truncate">{file.name}</span>
                      <span className="shrink-0 text-muted-foreground">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {isImporting && (
              <div className="space-y-2 rounded-md border bg-muted/20 p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate text-muted-foreground">{uploadStatus}</span>
                  <span className="font-medium">{Math.round(uploadProgress * 100)}%</span>
                </div>
                {uploadRunProgress && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>
                      {uploadRunProgress.completedUploads} / {uploadRunProgress.totalUploads} files
                    </span>
                    {uploadRunProgress.activeUploads > 0 && (
                      <span>{uploadRunProgress.activeUploads} active</span>
                    )}
                    <span>
                      {formatFileSize(uploadRunProgress.completedBytes)} /{" "}
                      {formatFileSize(uploadRunProgress.totalBytes)}
                    </span>
                    {uploadRunProgress.failedUploads > 0 && (
                      <span className="text-destructive">
                        {uploadRunProgress.failedUploads} failed
                      </span>
                    )}
                    {uploadRunProgress.currentFileNames.length > 0 && (
                      <span className="min-w-0 truncate">
                        {uploadRunProgress.currentFileNames.join(", ")}
                      </span>
                    )}
                  </div>
                )}
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${uploadProgress * 100}%` }}
                  />
                </div>
              </div>
            )}
            {!isImporting && hasRecoverableUploadState && (
              <div className="space-y-3 rounded-md border bg-muted/20 p-3 text-sm">
                {uploadedCount > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle2 className="size-4 text-emerald-600" />
                    <span>{uploadedCount} uploaded files will be reused on retry.</span>
                  </div>
                )}
                {uploadFailures.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-destructive">
                      <AlertCircle className="size-4" />
                      <span>{uploadFailures.length} uploads need retry.</span>
                    </div>
                    <div className="max-h-28 overflow-auto rounded-md border bg-background p-2 text-xs">
                      {uploadFailures.map((failure) => (
                        <div key={`${failure.videoIndex}-${failure.fileName}`} className="py-1">
                          <div className="font-medium">{failure.fileName}</div>
                          <div className="truncate text-muted-foreground">{failure.message}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <Button type="button" variant="ghost" size="sm" onClick={resetImportSession}>
                  <XCircle className="size-4" />
                  Clear upload state
                </Button>
              </div>
            )}
            <Button type="submit" disabled={isImporting || !manifestText.trim()} className="w-full">
              {isImporting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" />
              )}
              {uploadFailures.length > 0 ? "Retry Failed Uploads" : "Import Test"}
            </Button>
          </div>
        </div>
      </form>

      <div className="grid gap-3 sm:grid-cols-3">
        <MirageEvalMetric
          label="Open"
          value={tests.filter((test) => test.status === "open").length}
        />
        <MirageEvalMetric
          label="Draft"
          value={tests.filter((test) => test.status === "draft").length}
        />
        <MirageEvalMetric
          label="Closed"
          value={tests.filter((test) => test.status === "closed").length}
        />
      </div>

      {isLoading && (
        <div className="rounded-md border p-5 text-sm text-muted-foreground">Loading tests...</div>
      )}

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load Mirage evals</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {!isLoading && !error && tests.length === 0 && (
        <div className="rounded-md border p-5 text-sm text-muted-foreground">
          No eval tests found.
        </div>
      )}

      {tests.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Test</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Videos</TableHead>
                <TableHead>Votes</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tests.map((test) => (
                <TableRow key={test.testId}>
                  <TableCell>
                    <div className="min-w-0">
                      <Link
                        to="/videos/evals/$testId"
                        params={{ testId: test.testId }}
                        className="font-medium text-primary hover:underline"
                      >
                        {test.name}
                      </Link>
                      <div className="mt-1 font-mono text-xs text-muted-foreground">
                        {test.testId}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <MirageEvalStatusBadge status={test.status} />
                  </TableCell>
                  <TableCell>
                    {test.videoCount} / {test.clipCount} clips
                  </TableCell>
                  <TableCell>{test.voteCount}</TableCell>
                  <TableCell>{formatMirageEvalDate(test.updatedAt)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button asChild variant="outline" size="xs">
                        <Link to="/videos/evals/$testId/rank" params={{ testId: test.testId }}>
                          Rank
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="xs">
                        <Link to="/videos/evals/$testId/results" params={{ testId: test.testId }}>
                          Results
                        </Link>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

async function uploadEvalVideoWithRetries({
  bucket,
  file,
  onAttempt,
  onProgress,
  prefix,
}: {
  bucket: string;
  file: File;
  onAttempt: (attempt: number) => void;
  onProgress: (progress: number) => void;
  prefix: string;
}): Promise<{ assetId: string; attempts: number }> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt += 1) {
    onAttempt(attempt);
    try {
      const assetId = await uploadEvalVideo({
        bucket,
        file,
        prefix,
        onProgress,
      });
      return { assetId, attempts: attempt };
    } catch (error) {
      lastError = error;
      onProgress(0);
      if (attempt < MAX_UPLOAD_ATTEMPTS) {
        await sleep(750 * attempt);
      }
    }
  }
  if (lastError instanceof Error) {
    throw lastError;
  }
  throw new Error("Upload failed");
}

async function uploadEvalVideo({
  bucket,
  file,
  prefix,
  onProgress,
}: {
  bucket: string;
  file: File;
  prefix: string;
  onProgress: (progress: number) => void;
}): Promise<string> {
  const extension = file.name.split(".").pop() || "";
  const mimeType = file.type || extensionToMimeType(extension);
  const completeResponse = await uploadResumableFile({
    file,
    assetType: mimeTypeToResumableAssetType(mimeType),
    mimeType,
    bucket,
    prefix,
    retention: "long_term",
    makePublic: false,
    onProgress,
  });
  return completeResponse.asset_id;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
